import {Component, EventEmitter, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormGroup} from "@angular/forms";
import {UserService} from "../../../../../../lib/services/user.service";
import {HcpService} from "../../../../../../lib/services/hcp.service";
import {v4 as uuidv4} from 'uuid';
import {HealthcareParty} from "@icure/api";
import * as _ from 'lodash';
import {MessageTypeEnum} from "../../../database-group/database-group.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {User} from "@icure/api/icc-api/model/User";
import {MatCheckboxChange} from "@angular/material/checkbox";

interface ParentIdInfos{
    hcpId: string;
    name: string;
}

interface RoleInfos{
  id: string;
  defaultChecked: boolean;
  available: boolean;
  description: string;
}

interface UserDialogData {
  groupInfos?: { id?: string };
  listOfHcps?: HealthcareParty[];
}

@Component({
  selector: 'ms-user-dialog',
  templateUrl: './user-dialog.component.html',
  styleUrls: ['./user-dialog.component.scss'],
  standalone: false
})
export class UserDialogComponent implements OnInit{
  public onCreatedUser = new EventEmitter();
  public userForm: FormGroup = new FormGroup({});
  public availableParentId: ParentIdInfos[] = [];
  public newUserInfos: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    passwordTemp: string;
    parentId: string;
    ssin: string;
    nihii: string;
    roles: string[];
  } = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordTemp: '',
    parentId: '',
    ssin: '',
    nihii: '',
    roles: ['MS_PRACTICIAN']
  }

  public availableRoles: RoleInfos[]  = [
    {
      id: 'MS_STUDENT_PRACTICIAN',
      defaultChecked: false,
      available: true,
      description: 'Etudiant en médecine'
    },
    {
      id: 'MS_PRESCRIPTOR',
      defaultChecked: false,
      available: true,
      description: 'Prescripteur'
    },
    {
      id: 'MS_USER_MANAGER',
      defaultChecked: false,
      available: true,
      description: 'Gestionnaire d\'utilisateurs'
    },
    {
      id: 'MS_SECRETARY',
      defaultChecked: false,
      available: true,
      description: 'Secrétaire'
    },
    {
      id: 'MS_PRACTICIAN',
      defaultChecked: true,
      available: true,
      description: 'Praticien'
    },
    {
      id: 'MS_ADMIN',
      defaultChecked: false,
      available: true,
      description: 'Administrateur'
    }
  ];
  constructor(
    public dialogRef: MatDialogRef<UserDialogComponent>,
     @Inject(MAT_DIALOG_DATA) public data: UserDialogData,
    private userService: UserService,
    private hcpService: HcpService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    const hcpsById: Map<string, string> = new Map(
      (this.data?.listOfHcps ?? [])
        .map((hcp: HealthcareParty): [string, string] | null => {
          if (!hcp?.id) {
            return null;
          }

          return [hcp.id, this.getHcpLabel(hcp)];
        })
        .filter((entry: [string, string] | null): entry is [string, string] => !!entry),
    );

    const parentCandidates: ParentIdInfos[] = (this.data?.listOfHcps ?? [])
      .map((hcp: HealthcareParty): ParentIdInfos | null => {
        if (!hcp?.parentId) {
          return null;
        }

        return {
          hcpId: hcp.parentId,
          name: hcpsById.get(hcp.parentId) ?? hcp.parentId,
        };
      })
      .filter((parentId: ParentIdInfos | null): parentId is ParentIdInfos => !!parentId?.hcpId);

    this.availableParentId = _.uniqBy(parentCandidates, 'hcpId');
    this.newUserInfos.parentId = this.availableParentId[0]?.hcpId ?? '';
  }

  private getHcpLabel(hcp: HealthcareParty): string {
    const firstNameLastName: string = `${hcp?.firstName ?? ''} ${hcp?.lastName ?? ''}`.trim();

    return firstNameLastName || hcp?.name || hcp?.id || '';
  }

  public roleChanged(e: MatCheckboxChange): void{
    if(e.checked === true){
      this.newUserInfos.roles.push(e.source.value);
    }else{
      this.newUserInfos.roles.splice(this.newUserInfos.roles.indexOf(e.source.value), 1);
    }
  }

  public async createUser(){
    if(this.newUserInfos?.firstName !== '' && this.newUserInfos?.lastName !== '' && this.newUserInfos?.email !=='' && (this.newUserInfos?.password !== '' || this.newUserInfos?.passwordTemp !== '') && this.newUserInfos?.roles.length > 0 ){
      try {
        const newInstanceOfHcp = {
          id: uuidv4(),
          firstName: this.newUserInfos?.firstName,
          lastName: this.newUserInfos?.lastName,
          ssin: this.newUserInfos?.ssin || '',
          nihii: this.newUserInfos?.nihii || '',
          ...(this.newUserInfos?.parentId ? { parentId: this.newUserInfos.parentId } : {}),
        }

        const groupId: string = this.data?.groupInfos?.id ?? '';
        const hcp = await this.hcpService.addHcpInGroup(groupId, newInstanceOfHcp);
        const hcpId: string = hcp?.id ?? '';
        await this.userService.addUserInGroup(groupId, {
          id: uuidv4(),
          healthcarePartyId: hcpId,
          name: `${this.newUserInfos?.firstName} ${this.newUserInfos?.lastName}`,
          email: this.newUserInfos.email,
          login: this.newUserInfos.email,
          autoDelegations: { ["all"]: [hcpId, newInstanceOfHcp.parentId ? newInstanceOfHcp.parentId : null].filter((id): id is string => !!id) },
          type: User.TypeEnum.Database,
          status: User.StatusEnum.ACTIVE,
          passwordHash: this.newUserInfos?.password,
          roles: this.newUserInfos?.roles,
          applicationTokens: this.newUserInfos?.passwordTemp !== '' ? {"tmp" : this.newUserInfos.passwordTemp} : {}
        });

        this.closeDialog();
        this.onCreatedUser.emit({
          status: 'success',
        })
        this.displayWarningMessage(`L'utilisateur: ${this.newUserInfos?.firstName} ${this.newUserInfos?.lastName} a bien été créé dans la db`, MessageTypeEnum.SUCCESS);
      }catch {
        this.closeDialog();
        this.displayWarningMessage(`Erreur de la création de l'utilisateur ${this.newUserInfos?.firstName} ${this.newUserInfos?.lastName}`, MessageTypeEnum.ERROR);
      }

    }
  }

  public closeDialog(){
    this.dialogRef.close();
  }

  private displayWarningMessage(message: string, messageType: MessageTypeEnum){
    this.snackBar.open(message, '', {
      duration: 5000,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: [messageType]
    });
  }

}
